import { CommonModule } from '@angular/common';
import { Component, inject, Signal, computed, Optional } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FocusLevel } from '@app/_enums/focus-trainer.enum';
import { DataService } from '@app/_services/data.service';
import { SettingsService } from '@app/_services/settings.service';
import { FocusStore } from '@app/_stores';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { MatMenuModule } from '@angular/material/menu';
import { CollaboratorComponent } from '@app/components/addons/collaborators/collaborator/collaborator.component';
import { FeatureGuideDialogComponent } from '@app/components/common/feature-guides/feature-guide-dialog/feature-guide-dialog.component';
import { CacheService } from '@app/_services/cache.service';
import { Focus, FocusAttemptData } from '@app/_interfaces/focus-trainer.interface';
import { MembersModel } from '@app/_interfaces/generic.interface';
import { getCollaboratorCount } from '@app/_utils/members-model.utils';
import { ModalService } from '@app/_services/modal.service';
import { CustomBottomSheetRef, CustomBottomSheetService } from '@app/_services/custom-bottom-sheet.service';
import { AlertService } from '@app/_services/alert.service';
import { getLocalDateString as getDateString, toDate } from '@app/_services/date';
import * as htmlToImage from 'html-to-image';

@Component({
  selector: 'app-focus-level',
  imports: [
    CommonModule,
    SvgComponent,
    MatMenuModule
  ],
  templateUrl: './focus-level.component.html',
  styleUrl: './focus-level.component.scss'
})

export class FocusLevelComponent {

  focusLevels: FocusLevel[] = [FocusLevel.ONE, FocusLevel.TWO, FocusLevel.THREE];
  focusConfig: Signal<Record<FocusLevel, { title: string, instruction: string }>> = this.dataService.focusConfig;
  readonly focusStore = inject(FocusStore);
  myFocus: Signal<Focus | null> = computed(() => {
    return this.focusStore.myFocus();
  });
  members: Signal<MembersModel> = computed(() => {
    const myFocus = this.myFocus();
    if (!myFocus) return { ownerEmail: '', memberUids: [], collaborators: {} };
    return myFocus.members || { ownerEmail: '', memberUids: [], collaborators: {} };
  });
  collabCount: Signal<number> = computed(() => {
    return getCollaboratorCount(this.members());
  });

  todayDateString: Signal<string> = computed(() => {
    return this.cc.today().dateString;
  });

  todayAttempts: Signal<FocusAttemptData[]> = computed(() => {
    const myFocus = this.myFocus();
    if (!myFocus?.attempts) return [];
    const attempts = Object.values(myFocus.attempts);
    return attempts.filter(a => getDateString(toDate(a.startAt)) === this.todayDateString());
  });

  hasEntriesToday: Signal<boolean> = computed(() => {
    return this.todayAttempts().length > 0;
  });

  highestAttempt: Signal<{ score: number; duration: number }> = computed(() => {
    const attempts = this.todayAttempts();
    if (attempts.length === 0) return { score: 0, duration: 0 };

    const highest = attempts.reduce((a, b) => {
      const scoreA = a.shapeCount * a.level;
      const scoreB = b.shapeCount * b.level;
      return scoreA > scoreB ? a : b;
    });

    return {
      score: highest.shapeCount * highest.level,
      duration: highest.shapeCount * 6,
    };
  });

  constructor(
    public ss: SettingsService,
    @Optional() public dialogRef: MatDialogRef<FocusLevelComponent>,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<FocusLevelComponent> | null,
    private router: Router,
    public dialog: MatDialog,
    private dataService: DataService,
    public cc: CacheService,
    private modalService: ModalService,
    private customBottomSheetService: CustomBottomSheetService,
    private alertService: AlertService,
  ) { }

  selectLevel(level: number) {
    this.closeDialog();
    this.closeAll();
    this.router.navigate(['/focus-tracker', level]);
  };

  closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
  }

  private closeAll(result?: any): void {
    if (this.dialog && !this.cc.isNarrowScreen()) {
      this.dialog.closeAll();
    } else if (this.customBottomSheetService) {
      this.customBottomSheetService.dismissAll();
    }
  }

  isCollab: Signal<boolean> = computed(() => {
    return this.collabCount() > 0;
  });

  addCollaborator() {
    this.modalService.openLevel2(
      CollaboratorComponent,
      {
        collection: 'focus',
        entityData: this.myFocus(),
        isPublic: false,
      },
      { disableClose: true }
    );
  }

  openFeatureInfo() {
    this.modalService.openLevel1(
      FeatureGuideDialogComponent,
      {
        entityType: 'focus'
      },
      { disableClose: true, autoFocus: false }
    );
  }

  downloadImage() {
    const highestAttempt = this.highestAttempt();
    const score = highestAttempt.score;
    const totalMinutes = (highestAttempt.duration / 60).toFixed(2);
    const userName = this.cc.user.name || this.cc.user.email?.split('@')[0] || '';
    const durationText = this.ss.interpolateText('focus_durationInMinute', { minute: totalMinutes });

    // 1. Create off-screen wrapper
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.left = '-9999px';
    wrapper.style.top = '0';
    wrapper.style.width = '308px';
    document.body.appendChild(wrapper);

    // 2. Outer container (card background)
    const container = document.createElement('div');
    container.style.width = '100%';
    container.style.backgroundColor = this.ss.theme().color5;
    container.style.borderRadius = '24px';
    container.style.padding = '16px';
    container.style.boxSizing = 'border-box';
    container.style.fontFamily = 'Roboto, sans-serif';
    wrapper.appendChild(container);

    // 3. Inner colored box (score section)
    const scoreBox = document.createElement('div');
    scoreBox.style.width = '100%';
    scoreBox.style.backgroundColor = this.ss.theme().color1;
    scoreBox.style.borderRadius = '12px';
    scoreBox.style.padding = '20px';
    scoreBox.style.boxSizing = 'border-box';
    scoreBox.style.textAlign = 'center';
    container.appendChild(scoreBox);

    // Title: "Focus Score"
    const title = document.createElement('p');
    title.innerText = this.ss.texts()['focus_focusScore'];
    title.style.font = '18px/24px Roboto, sans-serif';
    title.style.fontWeight = '700';
    title.style.color = this.ss.theme().color12;
    title.style.margin = '0 0 8px 0';
    scoreBox.appendChild(title);

    // Username
    const nameEl = document.createElement('p');
    nameEl.innerText = userName;
    nameEl.style.font = '12px/16px Roboto, sans-serif';
    nameEl.style.color = this.ss.theme().color12;
    nameEl.style.margin = '0 0 20px 0';
    scoreBox.appendChild(nameEl);

    // Big score number
    const scoreEl = document.createElement('p');
    scoreEl.innerText = score.toString();
    scoreEl.style.font = '48px/56px Roboto, sans-serif';
    scoreEl.style.fontWeight = '800';
    scoreEl.style.color = this.ss.theme().color12;
    scoreEl.style.margin = '0 0 16px 0';
    scoreBox.appendChild(scoreEl);

    // Duration in minutes
    const durationEl = document.createElement('p');
    durationEl.innerText = durationText;
    durationEl.style.font = '12px/16px Roboto, sans-serif';
    durationEl.style.color = this.ss.theme().color12;
    durationEl.style.margin = '0';
    scoreBox.appendChild(durationEl);

    // 4. Footer section
    const footer = document.createElement('div');
    footer.style.paddingTop = '16px';
    container.appendChild(footer);

    // Footer top row: "Focus Tracker" + "Free" badge
    const footerTop = document.createElement('div');
    footerTop.style.display = 'flex';
    footerTop.style.justifyContent = 'space-between';
    footerTop.style.alignItems = 'center';
    footer.appendChild(footerTop);

    const focusTrackerLabel = document.createElement('p');
    focusTrackerLabel.innerText = this.ss.texts()['common_focusTracker'];
    focusTrackerLabel.style.font = '18px/24px Roboto, sans-serif';
    focusTrackerLabel.style.fontWeight = '700';
    focusTrackerLabel.style.color = this.ss.theme().color8;
    focusTrackerLabel.style.margin = '0';
    footerTop.appendChild(focusTrackerLabel);

    const freeBadge = document.createElement('span');
    freeBadge.innerText = this.ss.texts()['common_subscriptionFree'];
    freeBadge.style.font = '14px/18px Roboto, sans-serif';
    freeBadge.style.fontWeight = '700';
    freeBadge.style.color = this.ss.theme().color12;
    freeBadge.style.backgroundColor = this.ss.theme().color1;
    freeBadge.style.borderRadius = '12px';
    freeBadge.style.padding = '2px 16px';
    footerTop.appendChild(freeBadge);

    // Footer bottom row: website URL
    const footerBottom = document.createElement('div');
    footerBottom.style.display = 'flex';
    footerBottom.style.justifyContent = 'flex-end';
    footerBottom.style.paddingTop = '16px';
    footer.appendChild(footerBottom);

    const url = document.createElement('p');
    url.innerText = 'www.mevolve.app';
    url.style.font = '14px/18px Roboto, sans-serif';
    url.style.fontWeight = '700';
    url.style.color = this.ss.theme().color7;
    url.style.margin = '0';
    footerBottom.appendChild(url);

    // 5. Convert to image and download
    htmlToImage.toPng(container, {
      backgroundColor: this.ss.theme().color5,
      pixelRatio: 2,
      skipFonts: false,
    })
      .then(dataUrl => {
        const link = document.createElement('a');
        link.download = 'focus-score.png';
        link.href = dataUrl;
        link.click();
        this.alertService.toast('imageDownloaded_content');
        document.body.removeChild(wrapper);
      })
      .catch(err => {
        console.error('Image conversion failed:', err);
        document.body.removeChild(wrapper);
      });
  }
}
